"use client";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { ProjectSiteGlyph } from "@/ui/client/ProjectSiteGlyph";
import { fetchWithCsrf } from "@/ui/client/fetchWithCsrf";
import { authClient } from "@/ui/client/authClient";
import type {
  LayoutUser,
  SidebarFeaturedDeployment,
  SidebarProjectDeploymentStatus,
  SidebarProjectSummary
} from "@/ui/layoutUser";
import {
  Activity,
  ChevronDown,
  CirclePlay,
  FolderKanban,
  HardDrive,
  LayoutDashboard,
  Loader2,
  LogOut,
  Rocket,
  Settings2,
  Shield,
  X,
  XCircle
} from "lucide-react";
import { useEffect, useMemo, useState, useSyncExternalStore, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "@/spa/routerCompat";

const softSpringEasing = "cubic-bezier(0.25, 1.1, 0.4, 1)";

export type DeployherSidebarProps = {
  pathname: string;
  user?: LayoutUser | null;
  sidebarProjects?: SidebarProjectSummary[];
  sidebarContext?: {
    project?: {
      id: string;
      name: string;
      siteIconUrl: string | null;
      previewUrl: string | null;
    } | null;
    deployment?: SidebarFeaturedDeployment | null;
  };
};

const signOutFromApi = async (event: React.FormEvent<HTMLFormElement>, failedMessage: string): Promise<void> => {
  event.preventDefault();
  try {
    const result = await authClient.signOut();
    if (result.error) {
      window.alert(failedMessage);
      return;
    }
    window.location.href = "/";
  } catch {
    window.alert(failedMessage);
  }
};

type NavLink = {
  href: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  match?: (pathname: string) => boolean;
};

type NavGroup = {
  label: string;
  items: NavLink[];
};

const navIsActive = (pathname: string, item: NavLink): boolean => {
  if (item.match) return item.match(pathname);
  return pathname === item.href || pathname.startsWith(`${item.href}/`);
};

const projectIdFromPathname = (pathname: string): string | null => {
  const match = pathname.match(/^\/projects\/([^/]+)/);
  if (!match?.[1] || match[1] === "new") return null;
  return match[1];
};

const isProjectScopedPath = (pathname: string, sidebarContext: DeployherSidebarProps["sidebarContext"]): boolean => {
  if (sidebarContext?.project?.id) return true;
  if (pathname.startsWith("/deployments/")) return true;
  return pathname === "/projects" || pathname.startsWith("/projects/");
};

const subscribeShellCollapsed = (onChange: () => void) => {
  if (typeof document === "undefined") return () => {};
  const shell = document.getElementById("deployher-shell");
  if (!shell) return () => {};
  onChange();
  const observer = new MutationObserver(onChange);
  observer.observe(shell, { attributes: true, attributeFilter: ["class"] });
  return () => observer.disconnect();
};

const getShellCollapsed = (): boolean => {
  if (typeof document === "undefined") return false;
  return document.getElementById("deployher-shell")?.classList.contains("sidebar-collapsed") ?? false;
};

const getServerCollapsed = () => false;

const useShellSidebarCollapsed = () =>
  useSyncExternalStore(subscribeShellCollapsed, getShellCollapsed, getServerCollapsed);

const CollapsedSidebarTooltip = ({ label, children }: { label: string; children: ReactElement }) => {
  const collapsed = useShellSidebarCollapsed();
  if (!collapsed) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8} className="max-w-[min(20rem,calc(100vw-4rem))]">
        <span className="block truncate">{label}</span>
      </TooltipContent>
    </Tooltip>
  );
};

const sidebarProjectStatusDotClass = (status: SidebarProjectDeploymentStatus | null): string => {
  switch (status) {
    case "success":
      return "bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.55)]";
    case "failed":
    case "image_missing":
    case "ensure_failed":
      return "bg-red-400 shadow-[0_0_10px_rgba(248,113,113,0.55)]";
    case "building":
      return "bg-sky-400 shadow-[0_0_10px_rgba(56,189,248,0.55)]";
    case "queued":
    case "recovering":
      return "bg-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.55)]";
    default:
      return "bg-sidebar-foreground/30 shadow-none";
  }
};

const sidebarStatusAriaKey = (status: SidebarProjectDeploymentStatus | null): string => {
  switch (status) {
    case null:
      return "sidebar.statusNoDeploy";
    case "success":
      return "sidebar.statusLive";
    case "failed":
      return "sidebar.statusFailed";
    case "building":
      return "sidebar.statusBuilding";
    case "queued":
      return "sidebar.statusQueued";
    case "recovering":
      return "sidebar.statusRecovering";
    case "image_missing":
      return "sidebar.statusImageMissing";
    case "ensure_failed":
      return "sidebar.statusEnsureFailed";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
};

const SidebarProjectStatusDot = ({ status }: { status: SidebarProjectDeploymentStatus | null }) => {
  const { t } = useTranslation();
  return (
    <span
      className={cn("size-1.5 shrink-0 rounded-full animate-pulse-slow", sidebarProjectStatusDotClass(status))}
      aria-label={t(sidebarStatusAriaKey(status))}
      title={t(sidebarStatusAriaKey(status))}
    />
  );
};

const SidebarGroupLabel = ({
  children,
  className,
  id
}: {
  children: string;
  className?: string;
  id?: string;
}) => (
  <p
    id={id}
    className={cn(
      "deployher-sidebar-label shrink-0 px-2 pb-1.5 pt-3 text-[0.6875rem] font-medium text-sidebar-foreground/55 transition-[margin,opacity] duration-200 group-[.sidebar-collapsed]/shell:pointer-events-none group-[.sidebar-collapsed]/shell:-mt-6 group-[.sidebar-collapsed]/shell:opacity-0",
      className
    )}
  >
    {children}
  </p>
);

const treeLeafClassName = (active: boolean, opts?: { danger?: boolean }) =>
  cn(
    "flex h-7 min-w-0 items-center gap-2 rounded-md px-2 text-[0.8125rem] outline-none ring-sidebar-ring transition-colors hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground focus-visible:ring-2 no-underline hover:no-underline [&>svg]:size-3.5 [&>svg]:shrink-0",
    opts?.danger && !active ? "text-amber-600/95 dark:text-amber-400/90" : "",
    active
      ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
      : "text-sidebar-foreground/90"
  );

const SidebarLink = ({
  item,
  pathname,
  muted = false
}: {
  item: NavLink;
  pathname: string;
  muted?: boolean;
}) => {
  const active = navIsActive(pathname, item);
  const Icon = item.icon;
  return (
    <li data-slot="sidebar-menu-item">
      <CollapsedSidebarTooltip label={item.label}>
        <Link
          to={item.href}
          aria-current={active ? "page" : undefined}
          data-active={active ? "true" : "false"}
          data-slot="sidebar-menu-button"
          className={cn(
            "deployher-sidebar-link peer/menu-button relative flex h-8 items-center gap-2 overflow-hidden rounded-md px-2 text-left text-[0.8125rem] outline-none ring-sidebar-ring transition-[width,padding,background-color,color,box-shadow] duration-200 hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent no-underline hover:no-underline group-[.sidebar-collapsed]/shell:size-8 group-[.sidebar-collapsed]/shell:justify-center group-[.sidebar-collapsed]/shell:p-2 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[active=true]:shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--sidebar-foreground)_8%,transparent)] before:absolute before:left-0 before:top-1/2 before:h-4 before:w-0.5 before:-translate-y-1/2 before:rounded-r-full before:bg-sidebar-foreground before:opacity-0 before:transition-opacity data-[active=true]:before:opacity-100 group-[.sidebar-collapsed]/shell:before:hidden [&>svg]:size-4 [&>svg]:shrink-0",
            muted && !active ? "text-sidebar-foreground/80" : "text-sidebar-foreground"
          )}
        >
          {Icon ? <Icon className="shrink-0" aria-hidden /> : <span className="size-4 shrink-0" aria-hidden />}
          <span className="deployher-sidebar-label truncate group-[.sidebar-collapsed]/shell:sr-only">{item.label}</span>
        </Link>
      </CollapsedSidebarTooltip>
    </li>
  );
};

const SidebarProjectDeployButton = ({
  projectId,
  onError
}: {
  projectId: string;
  onError?: (message: string | null) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const handleDeploy = async () => {
    setBusy(true);
    onError?.(null);
    try {
      const response = await fetchWithCsrf(`/api/projects/${projectId}/deployments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });
      const data = (await response.json().catch(() => ({}))) as { id?: string; error?: string };
      if (!response.ok) {
        throw new Error(data.error ?? t("sidebar.failedCreateDeployment"));
      }
      if (data.id) {
        navigate(`/deployments/${data.id}`);
        return;
      }
      throw new Error(t("sidebar.deploymentNoId"));
    } catch (err) {
      onError?.(err instanceof Error ? err.message : t("sidebar.deployFailed"));
    } finally {
      setBusy(false);
    }
  };

  const label = busy ? t("sidebar.deploying") : t("sidebar.deployThisProject");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void handleDeploy();
          }}
          disabled={busy}
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-colors hover:bg-sidebar-accent/70 hover:text-sidebar-foreground focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-60"
          aria-label={label}
        >
          {busy ? (
            <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />
          ) : (
            <Rocket className="size-3.5 shrink-0" aria-hidden />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
};

const SidebarDeploymentRowIcon = ({ deployment }: { deployment: SidebarFeaturedDeployment }) => {
  if (deployment.sidebarRole === "failed") {
    return <XCircle className="size-3.5 shrink-0 text-red-400/90" aria-hidden />;
  }
  if (deployment.sidebarRole === "live") {
    return <CirclePlay className="size-3.5 shrink-0" aria-hidden />;
  }
  if (deployment.status === "building") {
    return <Loader2 className="size-3.5 shrink-0 animate-spin" aria-hidden />;
  }
  return <CirclePlay className="size-3.5 shrink-0 opacity-80" aria-hidden />;
};

const deploymentRowLabel = (
  deployment: SidebarFeaturedDeployment,
  t: (key: string, opts?: Record<string, string>) => string
): string => {
  switch (deployment.sidebarRole) {
    case "live":
      return t("sidebar.liveDeployment");
    case "failed":
      return t("sidebar.lastFailedDeployment");
    case "in_progress":
      return t("sidebar.latestRun");
    default: {
      const _exhaustive: never = deployment.sidebarRole;
      return _exhaustive;
    }
  }
};

const ProjectSettingsNest = ({
  projectId,
  pathname,
  showDanger
}: {
  projectId: string;
  pathname: string;
  showDanger: boolean;
}) => {
  const { t } = useTranslation();
  const onSettings =
    pathname === `/projects/${projectId}/settings` ||
    pathname.startsWith(`/projects/${projectId}/settings/`);
  const [open, setOpen] = useState(onSettings);

  useEffect(() => {
    if (onSettings) setOpen(true);
  }, [onSettings]);

  const settingsLinks: Array<{
    href: string;
    label: string;
    match: (p: string) => boolean;
    danger?: boolean;
  }> = [
    {
      href: `/projects/${projectId}/settings`,
      label: t("sidebar.generalSettings"),
      match: (p) => p === `/projects/${projectId}/settings`
    },
    {
      href: `/projects/${projectId}/settings/env`,
      label: t("sidebar.environmentVariables"),
      match: (p) => p === `/projects/${projectId}/settings/env`
    },
    {
      href: `/projects/${projectId}/settings/domains`,
      label: t("sidebar.domains"),
      match: (p) => p === `/projects/${projectId}/settings/domains`
    },
    {
      href: `/projects/${projectId}/settings/members`,
      label: t("sidebar.members"),
      match: (p) => p === `/projects/${projectId}/settings/members`
    },
    ...(showDanger
      ? [
          {
            href: `/projects/${projectId}/settings/danger`,
            label: t("sidebar.dangerZone"),
            match: (p: string) => p === `/projects/${projectId}/settings/danger`,
            danger: true
          }
        ]
      : [])
  ];

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className={cn(
          treeLeafClassName(onSettings),
          "w-full justify-between data-[state=open]:bg-sidebar-accent/50"
        )}
        aria-label={open ? t("sidebar.collapseSettings") : t("sidebar.expandSettings")}
      >
        <span className="flex min-w-0 items-center gap-2">
          <Settings2 className="shrink-0" aria-hidden />
          <span className="truncate">{t("common.settings")}</span>
        </span>
        <ChevronDown
          className={cn("size-3.5 shrink-0 transition-transform duration-200", open ? "rotate-180" : "rotate-0")}
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-3 mt-0.5 flex flex-col gap-0.5 border-l border-sidebar-border/80 pl-2" role="list">
          {settingsLinks.map((link) => {
            const active = link.match(pathname);
            return (
              <Link
                key={link.href}
                to={link.href}
                role="listitem"
                aria-current={active ? "page" : undefined}
                className={treeLeafClassName(active, { danger: link.danger })}
              >
                <span className="truncate">{link.label}</span>
              </Link>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const ProjectScopeChildren = ({
  projectId,
  pathname,
  deployment,
  showDanger
}: {
  projectId: string;
  pathname: string;
  deployment: SidebarFeaturedDeployment | null | undefined;
  showDanger: boolean;
}) => {
  const { t } = useTranslation();
  const overviewHref = `/projects/${projectId}`;
  const overviewActive = pathname === overviewHref;
  const observabilityHref = `/projects/${projectId}/observability`;
  const observabilityActive = pathname === observabilityHref;
  const deploymentActive = deployment != null && pathname === `/deployments/${deployment.id}`;

  return (
    <div className="ml-3 mt-0.5 flex flex-col gap-0.5 border-l border-sidebar-border/80 pl-2" role="list">
      <Link
        to={overviewHref}
        role="listitem"
        aria-current={overviewActive ? "page" : undefined}
        className={treeLeafClassName(overviewActive)}
      >
        <span className="truncate">{t("sidebar.overview")}</span>
      </Link>
      <Link
        to={observabilityHref}
        role="listitem"
        aria-current={observabilityActive ? "page" : undefined}
        className={treeLeafClassName(observabilityActive)}
      >
        <Activity className="shrink-0" aria-hidden />
        <span className="truncate">{t("sidebar.observability")}</span>
      </Link>
      <ProjectSettingsNest projectId={projectId} pathname={pathname} showDanger={showDanger} />
      {deployment ? (
        <Link
          to={`/deployments/${deployment.id}`}
          role="listitem"
          aria-current={deploymentActive ? "page" : undefined}
          aria-label={t("sidebar.openDeployment", {
            shortId: deployment.shortId,
            status: deployment.status
          })}
          className={cn(
            treeLeafClassName(deploymentActive),
            deployment.sidebarRole === "failed" && !deploymentActive
              ? "text-red-300/95 hover:text-red-200"
              : ""
          )}
        >
          <SidebarDeploymentRowIcon deployment={deployment} />
          <span className="min-w-0 truncate">
            {deploymentRowLabel(deployment, t)}
            <span className="ml-1.5 font-normal opacity-70">{deployment.shortId}</span>
          </span>
        </Link>
      ) : null}
    </div>
  );
};

const ProjectTreeRow = ({
  project,
  pathname,
  expanded,
  deployment
}: {
  project: SidebarProjectSummary;
  pathname: string;
  expanded: boolean;
  deployment: SidebarFeaturedDeployment | null | undefined;
}) => {
  const [deployError, setDeployError] = useState<string | null>(null);
  const inProject =
    pathname === `/projects/${project.id}` || pathname.startsWith(`/projects/${project.id}/`);
  const showDanger = project.accessRole == null || project.accessRole === "owner";

  return (
    <div role="listitem" className="min-w-0">
      <div
        className={cn(
          "flex min-w-0 items-center gap-0.5 rounded-md",
          expanded || inProject ? "bg-sidebar-accent/40" : ""
        )}
      >
        <Link
          to={`/projects/${project.id}`}
          aria-current={pathname === `/projects/${project.id}` ? "page" : undefined}
          className={cn(
            "flex h-7 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-[0.8125rem] outline-none ring-sidebar-ring transition-colors hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground focus-visible:ring-2 no-underline hover:no-underline",
            pathname === `/projects/${project.id}`
              ? "font-medium text-sidebar-accent-foreground"
              : "text-sidebar-foreground/90"
          )}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <ProjectSiteGlyph
              name={project.name}
              siteIconUrl={project.siteIconUrl}
              previewUrl={project.previewUrl ?? null}
              className="ring-0"
              letterClassName="flex size-4.5 items-center justify-center rounded-sm bg-sidebar-accent/50 text-[10px] font-medium text-sidebar-foreground/90"
            />
            <span className="min-w-0 truncate">{project.name}</span>
          </span>
        </Link>
        {expanded ? (
          <SidebarProjectDeployButton projectId={project.id} onError={setDeployError} />
        ) : null}
        <span className="pr-2">
          <SidebarProjectStatusDot status={project.deploymentStatus} />
        </span>
      </div>
      {expanded && deployError ? (
        <p className="mt-0.5 px-2 text-[0.6875rem] text-red-400/90" role="alert">
          {deployError}
        </p>
      ) : null}
      {expanded ? (
        <ProjectScopeChildren
          projectId={project.id}
          pathname={pathname}
          deployment={deployment}
          showDanger={showDanger}
        />
      ) : null}
    </div>
  );
};

const WorkspaceProjectsRow = ({
  pathname,
  sidebarProjects,
  projectsNav,
  sidebarContext,
  t
}: {
  pathname: string;
  sidebarProjects: SidebarProjectSummary[];
  projectsNav: NavLink;
  sidebarContext: DeployherSidebarProps["sidebarContext"];
  t: (key: string) => string;
}) => {
  const shellCollapsed = useShellSidebarCollapsed();
  const scoped = isProjectScopedPath(pathname, sidebarContext);
  const [nestOpen, setNestOpen] = useState(scoped);

  useEffect(() => {
    if (scoped) setNestOpen(true);
  }, [scoped]);

  const activeProjectId = sidebarContext?.project?.id ?? projectIdFromPathname(pathname);

  const projects = useMemo(() => {
    const list = [...sidebarProjects];
    const ctx = sidebarContext?.project;
    if (ctx && !list.some((p) => p.id === ctx.id)) {
      list.unshift({
        id: ctx.id,
        name: ctx.name,
        deploymentStatus: null,
        siteIconUrl: ctx.siteIconUrl,
        siteOgImageUrl: null,
        previewUrl: ctx.previewUrl
      });
    }
    return list;
  }, [sidebarProjects, sidebarContext?.project]);

  const projectsActive = navIsActive(pathname, projectsNav);
  const ProjectsIcon = projectsNav.icon;

  if (shellCollapsed || projects.length === 0) {
    return <SidebarLink item={projectsNav} pathname={pathname} />;
  }

  return (
    <li data-slot="sidebar-menu-item" className="list-none">
      <Collapsible open={nestOpen} onOpenChange={setNestOpen}>
        <div className="flex min-w-0 items-stretch gap-0.5">
          <CollapsedSidebarTooltip label={projectsNav.label}>
            <Link
              to={projectsNav.href}
              aria-current={pathname === "/projects" ? "page" : undefined}
              data-active={projectsActive ? "true" : "false"}
              data-slot="sidebar-menu-button"
              className={cn(
                "deployher-sidebar-link peer/menu-button relative flex h-8 min-w-0 flex-1 items-center gap-2 overflow-hidden rounded-md px-2 text-left text-[0.8125rem] outline-none ring-sidebar-ring transition-[width,padding,background-color,color,box-shadow] duration-200 hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent no-underline hover:no-underline data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[active=true]:shadow-[inset_0_0_0_1px_color-mix(in_oklab,var(--sidebar-foreground)_8%,transparent)] before:absolute before:left-0 before:top-1/2 before:h-4 before:w-0.5 before:-translate-y-1/2 before:rounded-r-full before:bg-sidebar-foreground before:opacity-0 before:transition-opacity data-[active=true]:before:opacity-100 [&>svg]:size-4 [&>svg]:shrink-0",
                projectsActive ? "text-sidebar-accent-foreground" : "text-sidebar-foreground"
              )}
            >
              {ProjectsIcon ? <ProjectsIcon className="shrink-0" aria-hidden /> : null}
              <span className="deployher-sidebar-label truncate">{projectsNav.label}</span>
            </Link>
          </CollapsedSidebarTooltip>
          <CollapsibleTrigger
            className={cn(
              "flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/70 outline-none ring-sidebar-ring hover:bg-sidebar-accent/70 hover:text-sidebar-foreground focus-visible:ring-2 data-[state=open]:bg-sidebar-accent/60 [&>svg]:size-4"
            )}
            aria-label={nestOpen ? t("sidebar.collapseProjectList") : t("sidebar.expandProjectList")}
          >
            <ChevronDown
              className={cn("transition-transform duration-200", nestOpen ? "rotate-180" : "rotate-0")}
              aria-hidden
            />
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <div
            className="ml-4 mt-1 flex flex-col gap-0.5 border-l border-sidebar-border/90 pl-2"
            role="list"
          >
            {projects.map((p) => (
              <ProjectTreeRow
                key={p.id}
                project={p}
                pathname={pathname}
                expanded={activeProjectId === p.id}
                deployment={activeProjectId === p.id ? sidebarContext?.deployment : null}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
};

const SidebarWorkspace = ({
  pathname,
  sidebarProjects,
  dashboardNav,
  projectsNav,
  sidebarContext,
  t
}: {
  pathname: string;
  sidebarProjects: SidebarProjectSummary[];
  dashboardNav: NavLink;
  projectsNav: NavLink;
  sidebarContext: DeployherSidebarProps["sidebarContext"];
  t: (key: string) => string;
}) => (
  <div>
    <SidebarGroupLabel>{t("nav.workspace")}</SidebarGroupLabel>
    <ul className="flex flex-col gap-0.5" data-slot="sidebar-menu">
      <SidebarLink item={dashboardNav} pathname={pathname} />
      <WorkspaceProjectsRow
        pathname={pathname}
        sidebarProjects={sidebarProjects}
        projectsNav={projectsNav}
        sidebarContext={sidebarContext}
        t={t}
      />
    </ul>
  </div>
);

const SidebarGroup = ({ group, pathname, muted }: { group: NavGroup; pathname: string; muted?: boolean }) => (
  <div>
    <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
    <ul className="flex flex-col gap-0.5" data-slot="sidebar-menu">
      {group.items.map((item) => (
        <SidebarLink key={item.href} item={item} pathname={pathname} muted={muted} />
      ))}
    </ul>
  </div>
);

export const DeployherSidebar = ({ pathname, user, sidebarProjects = [], sidebarContext }: DeployherSidebarProps) => {
  const { t } = useTranslation();

  const dashboardNav = useMemo(
    (): NavLink => ({
      href: "/dashboard",
      label: t("nav.dashboard"),
      icon: LayoutDashboard,
      match: (p) => p === "/dashboard" || p === "/home"
    }),
    [t]
  );

  const projectsNav = useMemo(
    (): NavLink => ({
      href: "/projects",
      label: t("nav.projects"),
      icon: FolderKanban,
      match: (p) =>
        p === "/projects" || p.startsWith("/projects/") || p.startsWith("/deployments/")
    }),
    [t]
  );

  const adminNav = useMemo(
    (): NavLink[] => [
      {
        href: "/health",
        label: t("nav.systemHealth"),
        icon: Activity,
        match: (p) => p === "/health"
      },
      {
        href: "/admin/ops",
        label: t("nav.platformOps"),
        icon: HardDrive,
        match: (p) => p === "/admin/ops" || p.startsWith("/admin/ops/")
      },
      {
        href: "/admin/examples",
        label: t("nav.adminWorkflow"),
        icon: Shield,
        match: (p) => p === "/admin/examples" || p.startsWith("/admin/examples/")
      }
    ],
    [t]
  );

  const adminGroups: NavGroup[] = useMemo(
    () => (user?.role === "operator" ? [{ label: t("nav.operations"), items: adminNav }] : []),
    [user?.role, adminNav, t]
  );

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        id="deployher-sidebar"
        data-mobile-open="false"
        data-slot="sidebar"
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex h-svh w-(--sidebar-width) -translate-x-full flex-col overflow-hidden border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-none transition-[transform,width] duration-300 ease-linear data-[mobile-open=true]:translate-x-0 md:z-30 md:translate-x-0 group-[.sidebar-collapsed]/shell:w-(--sidebar-width-icon)"
        )}
        style={{ transitionTimingFunction: softSpringEasing }}
      >
        <div className="flex h-12 shrink-0 items-center gap-2 border-b border-sidebar-border px-2.5 group-[.sidebar-collapsed]/shell:justify-center group-[.sidebar-collapsed]/shell:px-2">
          <CollapsedSidebarTooltip label={t("common.deployherBrand")}>
            <Link
              to="/dashboard"
              className="deployher-sidebar-brand flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-sm font-medium text-sidebar-foreground no-underline outline-none ring-sidebar-ring hover:bg-sidebar-accent/70 hover:no-underline focus-visible:ring-2 group-[.sidebar-collapsed]/shell:flex-none group-[.sidebar-collapsed]/shell:justify-center"
              aria-label={t("sidebar.deployherHome")}
            >
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-sidebar-primary text-[0.8125rem] font-semibold text-sidebar-primary-foreground shadow-sm">
                d
              </span>
              <span className="deployher-sidebar-label truncate text-[0.875rem] font-medium group-[.sidebar-collapsed]/shell:sr-only">
                {t("common.deployherBrand")}
              </span>
            </Link>
          </CollapsedSidebarTooltip>
          <button
            type="button"
            id="deployher-sidebar-close-mobile"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-sidebar-accent/70 focus-visible:ring-2 md:hidden"
            aria-label={t("sidebar.closeSidebar")}
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <div
          className="deployher-sidebar-scroll flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2 py-1.5"
          data-slot="sidebar-content"
        >
          <SidebarWorkspace
            pathname={pathname}
            sidebarProjects={sidebarProjects}
            dashboardNav={dashboardNav}
            projectsNav={projectsNav}
            sidebarContext={sidebarContext}
            t={t}
          />
          {adminGroups.map((group) => (
            <SidebarGroup key={group.label} group={group} pathname={pathname} muted />
          ))}
        </div>

        <div className="mt-auto shrink-0 border-t border-sidebar-border p-2" data-slot="sidebar-footer">
          {user ? (
            <>
              <CollapsedSidebarTooltip label={user.name ?? user.email}>
                <Link
                  to="/account"
                  className="deployher-sidebar-link mb-1 flex h-9 items-center gap-2 rounded-md px-2 text-[0.8125rem] text-sidebar-foreground/90 outline-none ring-sidebar-ring transition-colors hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground focus-visible:ring-2 no-underline hover:no-underline group-[.sidebar-collapsed]/shell:justify-center"
                  aria-label={t("sidebar.accountLink")}
                >
                  {user.image ? (
                    <img
                      src={user.image}
                      alt=""
                      width={24}
                      height={24}
                      className="size-6 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-[0.6875rem] font-medium">
                      {(user.name ?? user.email).charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className="deployher-sidebar-label min-w-0 truncate group-[.sidebar-collapsed]/shell:sr-only">
                    {user.name ?? user.email}
                  </span>
                </Link>
              </CollapsedSidebarTooltip>
              <form
                id="signout-form"
                className="group-[.sidebar-collapsed]/shell:px-0"
                onSubmit={(event) => void signOutFromApi(event, t("sidebar.signOutFailed"))}
              >
                <CollapsedSidebarTooltip label={t("common.signOut")}>
                  <button
                    type="submit"
                    className="flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[0.8125rem] text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground group-[.sidebar-collapsed]/shell:justify-center"
                    aria-label={t("common.signOut")}
                  >
                    <LogOut className="size-4 shrink-0" aria-hidden />
                    <span className="deployher-sidebar-label group-[.sidebar-collapsed]/shell:sr-only">
                      {t("common.signOut")}
                    </span>
                  </button>
                </CollapsedSidebarTooltip>
              </form>
            </>
          ) : (
            <Link
              to="/login"
              className="flex h-8 items-center justify-center rounded-md bg-sidebar-primary px-2 text-[0.8125rem] font-medium text-sidebar-primary-foreground no-underline hover:no-underline hover:opacity-90"
            >
              {t("sidebar.signInCta")}
            </Link>
          )}
        </div>

        <button
          type="button"
          id="deployher-sidebar-rail"
          tabIndex={-1}
          title={t("sidebar.toggleSidebarRail")}
          aria-label={t("sidebar.toggleSidebarRail")}
          className="absolute inset-y-0 right-0 z-20 hidden w-3 cursor-ew-resize border-0 bg-transparent p-0 md:block group-[.sidebar-collapsed]/shell:cursor-e-resize after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-transparent after:transition-colors hover:after:bg-sidebar-foreground/30"
        />
      </aside>
    </TooltipProvider>
  );
};
